import React, { ReactNode, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';

/** Panel Header yüksekliği (h-14) — sticky top ile eşleşmeli */
const STICKY_TOP_PX = 56;

export interface PageToolbarProps {
  /** Toolbar içeriği; butonlar, select'ler vb. dışarıdan verilir. */
  children: ReactNode;
  /** Ek className (opsiyonel) */
  className?: string;
  /**
   * Kart içinde değilse dört kenar border + rounded-xl.
   * Kart içindeyse (default) yalnızca border-b; sticky olunca dört kenar gelir.
   */
  standalone?: boolean;
}

/**
 * App alt sayfalarında (Appscreen, Version vb.) kullanılan ortak toolbar container.
 * Scroll'da panel header'ın (h-14) altında yapışır; sayfa üstündeyken kendi yerinde kalır.
 * Sticky iken border-radius kaldırılır. Parent kartlar için: has-[[data-stuck]]:rounded-t-none
 */
const PageToolbar: React.FC<PageToolbarProps> = ({
  children,
  className = '',
  standalone = false,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const update = () => {
      setStuck(el.getBoundingClientRect().top <= STICKY_TOP_PX + 0.5);
    };

    update();
    window.addEventListener('scroll', update, { passive: true, capture: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, []);

  return (
    <div
      ref={ref}
      data-stuck={stuck ? 'true' : undefined}
      style={stuck ? { borderRadius: 0 } : undefined}
      className={clsx(
        'sticky top-14 z-40 flex-shrink-0 flex flex-wrap items-center gap-3 px-4 py-2.5 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800',
        stuck || standalone
          ? 'border'
          : 'border-b',
        stuck
          ? '!rounded-none'
          : standalone
            ? 'rounded-xl'
            : 'rounded-t-xl',
        className
      )}
    >
      {children}
    </div>
  );
};

export default PageToolbar;
